import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, TrendingUp, ArrowRight, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { useContactInsights, useContactSuggestions } from '@/hooks/use-ai'
import type { HealthLabel } from '@/lib/types'

const healthColors: Record<HealthLabel, { bg: string; text: string; bar: string }> = {
  thriving: {
    bg: 'bg-chartreuse-soft',
    text: 'text-chartreuse-foreground dark:text-chartreuse',
    bar: 'bg-chartreuse',
  },
  healthy: {
    bg: 'bg-lilac-soft',
    text: 'text-lilac-foreground dark:text-lilac',
    bar: 'bg-lilac',
  },
  fading: {
    bg: 'bg-coral-soft',
    text: 'text-coral',
    bar: 'bg-coral/70',
  },
  neglected: {
    bg: 'bg-coral-soft',
    text: 'text-coral',
    bar: 'bg-coral',
  },
}

function getCollapseKey(contactId: string) {
  return `aime:ai-insights-collapsed:${contactId}`
}

export function AiInsightsCard({ contactId }: { contactId: string }) {
  const { t } = useTranslation()
  const [triggered, setTriggered] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(getCollapseKey(contactId)) === 'true'
  })

  const {
    data: insightsData,
    isLoading: insightsLoading,
    error: insightsError,
  } = useContactInsights(contactId, triggered)
  const { data: suggestionsData, isLoading: suggestionsLoading } = useContactSuggestions(
    contactId,
    triggered,
  )

  const toggleCollapse = () => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem(getCollapseKey(contactId), String(next))
  }

  // Not yet triggered — show generate button
  if (!triggered) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setTriggered(true)}
          className="border-primary/30 text-primary hover:bg-primary/5 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm font-medium transition-colors"
        >
          <Sparkles className="size-4" />
          {t('ai.generateInsights', 'Generate AI Insights')}
        </button>
      </div>
    )
  }

  // Loading state — skeleton mirrors the health-score card + suggestion cards
  if (insightsLoading || suggestionsLoading) {
    return (
      <div
        className="space-y-3"
        aria-busy="true"
        aria-label={t('ai.loadingInsights', 'Loading AI insights…')}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary size-4 animate-pulse" />
          <span className="text-sm font-semibold">{t('ai.insights')}</span>
        </div>
        {/* Health score card skeleton */}
        <div className="bg-muted/50 space-y-2 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-6 w-8" />
          </div>
          <Skeleton className="h-1.5 w-full rounded-full" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        {/* Suggestion cards skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border p-2.5">
              <Skeleton className="mt-0.5 size-3.5 shrink-0 rounded" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Error / insufficient data fallback
  if (insightsError) {
    const isInsufficientData =
      (insightsError as unknown as Record<string, unknown>)?.code === 'INSUFFICIENT_DATA' ||
      (insightsError instanceof Error && insightsError.message?.includes('INSUFFICIENT_DATA'))
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary size-4" />
          <span className="text-sm font-semibold">{t('ai.insights')}</span>
        </div>
        <div className="flex items-start gap-2 rounded-lg border border-dashed p-3">
          <AlertCircle className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <p className="text-muted-foreground text-xs">
            {isInsufficientData
              ? t(
                  'ai.insufficientData',
                  'Log at least 3 interactions to unlock AI insights for this contact.',
                )
              : t('ai.unavailable', 'AI insights are temporarily unavailable. Try again later.')}
          </p>
        </div>
      </div>
    )
  }

  const insights = insightsData?.insights
  const suggestions = suggestionsData?.suggestions

  if (!insights) return null

  const colors = healthColors[insights.label]

  return (
    <div className="space-y-3">
      <button onClick={toggleCollapse} className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary size-4" />
          <span className="text-sm font-semibold">{t('ai.insights')}</span>
        </div>
        {collapsed ? (
          <ChevronDown className="text-muted-foreground size-4" />
        ) : (
          <ChevronUp className="text-muted-foreground size-4" />
        )}
      </button>

      {!collapsed && (
        <>
          {/* Health score */}
          <div className={cn('rounded-lg p-3', colors.bg)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className={cn('size-4', colors.text)} />
                <span className={cn('text-sm font-medium capitalize', colors.text)}>
                  {t(`ai.health.${insights.label}`)}
                </span>
              </div>
              <span className={cn('text-lg font-bold', colors.text)}>{insights.score}</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-black/10 dark:bg-white/10">
              <div
                className={cn('h-full rounded-full transition-all', colors.bar)}
                style={{ width: `${insights.score}%` }}
              />
            </div>
            <p className="text-muted-foreground mt-2 text-xs">{insights.explanation}</p>
            {insights.factors.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {insights.factors.map((factor, i) => (
                  <li key={i} className="text-muted-foreground text-xs">
                    · {factor}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Suggestions */}
          {suggestions && suggestions.length > 0 && (
            <div className="space-y-2">
              <span className="text-muted-foreground text-xs font-medium">
                {t('ai.suggestedActions')}
              </span>
              {suggestions.slice(0, 3).map((suggestion, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border p-2.5">
                  <ArrowRight className="text-primary mt-0.5 size-3.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{suggestion.action}</p>
                    <p className="text-muted-foreground text-xs">{suggestion.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
